import './polyfills'

import type { FC, ReactElement } from 'react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { Helmet } from 'react-helmet'
import { configure } from 'mobx'
import { observer } from 'mobx-react-lite'
import { TransportProvider } from '@connectrpc/connect-query'
import { I18nProvider } from '@lingui/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import type { BrandKey } from '@jotta/config/brand'
import { useBrandName } from '@jotta/grpc-connect-client/config'
import {
  persistCustomerLanguage,
  useCustomerLocale,
} from '@jotta/grpc-connect-client/customer'
import { publicTransport } from '@jotta/grpc-connect-client/transport'
import { usePreventIosAutoZoom } from '@jotta/hooks/usePreventIosAutoZoom'
import type { Locale } from '@jotta/i18n'
import { useActivateLocale } from '@jotta/i18n'
import { Intercom } from '@jotta/intercom'
import { queryClient } from '@jotta/query'
import { RouterProvider } from '@jotta/router'
import { BaseBrandProvider } from '@jotta/ui/BrandThemeProvider'
import {
  CookieConsentContext,
  CookieConsentModal,
} from '@jotta/ui/CookieConsentModal'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { ServerEvents } from '@jotta/ui/ServerEvents'
import { ToastProvider } from '@jotta/ui/ToastProvider'
import type { BrandTheme } from '@jotta/ui/types/BrandTheme'
import { env } from '@jotta/utils/env'
import { initializeSentry } from '@jotta/utils/initializeSentry'

import { InAppBrowserBreakout } from './components/InAppBrowserBreakout'
import { router } from './routes/RootRoutes'

const App: FC<{ overrideLocale?: Locale }> = observer(function App({
  overrideLocale,
}) {
  const brandName = useBrandName()
  const customerLocale = useCustomerLocale()
  const { isSuccess, i18n } = useActivateLocale({
    overrideLocale: overrideLocale || customerLocale,
    persistLanguage: persistCustomerLanguage,
  })
  const [cookieConsentNeeded, setCookieConsentNeeded] = useState(false)
  usePreventIosAutoZoom()

  if (isSuccess) {
    return (
      <I18nProvider i18n={i18n}>
        <CookieConsentContext.Provider
          value={{
            container: undefined,
            disableUnconsentedCookieDetection: false,
            cookieConsentNeeded,
            requestCookieConsent: () => setCookieConsentNeeded(true),
          }}
        >
          <InAppBrowserBreakout>
            <Helmet
              defaultTitle={brandName}
              titleTemplate={`%s - ${brandName}`}
            >
              <meta lang={i18n.locale} />
            </Helmet>
            <Intercom />
            <RouterProvider
              router={router}
              fallbackElement={<LoadingOverlay id="RouterProvider" open />}
            />
            <CookieConsentModal />
            <ToastProvider />
            <ServerEvents />
          </InAppBrowserBreakout>
        </CookieConsentContext.Provider>
      </I18nProvider>
    )
  }
  return null
})

export function start(brand: BrandKey, element: ReactElement) {
  initializeSentry({
    brand,
    dsn: env.sentryDsn,
    environment: env.sentryEnv,
    releaseName: env.releaseName,
  })
  const container = document.getElementById('root')
  if (!container) {
    throw new Error('#root element missing')
  }
  const root = createRoot(container)
  root.render(element)
}

export function startApp(theme: BrandTheme, overrideLocale?: Locale) {
  if (process.env.NODE_ENV === 'development') {
    document.body.classList.add('envdevelopment')
    configure({
      enforceActions: 'always',
      computedRequiresReaction: true,
      reactionRequiresObservable: false,
      observableRequiresReaction: false,
      disableErrorBoundaries: false,
    })
  }

  start(
    theme.branding.brand,
    <TransportProvider transport={publicTransport}>
      <QueryClientProvider client={queryClient}>
        <BaseBrandProvider theme={theme}>
          <App overrideLocale={overrideLocale} />
        </BaseBrandProvider>
        <DevTools />
      </QueryClientProvider>
    </TransportProvider>,
  )
}

function DevTools() {
  if (process.env.NODE_ENV === 'development') {
    return createPortal(
      <span
        id="tsqd"
        style={{
          pointerEvents: 'auto',
        }}
      >
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-left"
          client={queryClient}
        />
      </span>,
      document.body,
    )
  }
}
